'use client';

import type { ElementType, FocusEvent, MouseEvent } from 'react';

import type { MenuListItemProps } from '@uikit/components/MenuList';
import { MenuListItem } from '@uikit/components/MenuList';

import { useFloatingTree, useListItem, useMergeRefs } from '@floating-ui/react';

import { useDropdownMenu } from './DropdownMenu';

type DropdownItemProps<T extends ElementType> = MenuListItemProps<T> & {
  label: string;
  disabled?: boolean;
};

export const DropdownItem = <T extends ElementType = 'button'>(
  { label, disabled, children, ref, ...props }: DropdownItemProps<T>,
) => {
  const menu = useDropdownMenu();
  const item = useListItem({ label: disabled ? null : label });
  const tree = useFloatingTree();
  const isActive = item.index === menu?.activeIndex;

  return (
    <MenuListItem
      {...props}
      ref={useMergeRefs([item.ref, ref])}
      role="menuitem"
      tabIndex={isActive ? 0 : -1}
      {...menu?.getItemProps({
        onClick(event: MouseEvent<HTMLButtonElement>) {
          props.onClick?.(event);
          tree?.events.emit('click');
        },
        onFocus(event: FocusEvent<HTMLButtonElement>) {
          props.onFocus?.(event);
        },
      })}
    >
      {children || label}
    </MenuListItem>
  );
};

export default DropdownItem;
